// Human in the loop fixtures (HUMAN_IN_THE_LOOP_HANDOFF §7). Copy is from the boards.
import type { DoneItem, HitlItem, WaitingPage } from './types';
import { PMS } from '@/features/shared/pms';

export const HITL_DONE_30 = 11;

export function buildWaitingPages(): WaitingPage[] {
  return [
    { page: 'Claims', route: '/claims', count: 12, what: 'claims to confirm before sending' },
    { page: 'Posting', route: '/posting', count: 9, what: 'payments to confirm before posting' },
    { page: 'Pre-auths', route: '/pre-auths', count: 3, what: 'pre-auths to send or not' },
    { page: 'Denied & underpaid', route: '/denied', count: 3, what: 'appeals to sign and mail' },
  ];
}

export function buildItems(): HitlItem[] {
  return [
    // Today · 1 — access (a PASSWORD RESET, never a one-time code · D134)
    {
      id: 'HL-METLIFE',
      kind: 'access',
      kindLabel: 'Give ELVA access',
      group: 'now',
      title: 'Reset MetLife’s portal password',
      metaLine: 'MetLife provider portal · password expired Oct 14',
      deadlineText: 'Since Oct 14',
      deadlineWarn: true,
      why: 'MetLife made the portal password expire. ELVA can’t sign in for remittances and claim status until someone resets it on MetLife’s site and enters the new one here. Meanwhile ELVA calls MetLife for what it needs.',
      tries: [
        { time: 'Oct 14, 9:30 AM', channel: 'Saved login', result: 'MetLife rejected it — the password has expired.', ok: false, logEntryId: 'log-portal-metlife' },
        { time: 'Oct 15, 7:41 AM', channel: 'Call to MetLife', result: 'ELVA called instead and read the 2 claim statuses it needed.', ok: true, logEntryId: 'log-call-metlife' },
      ],
      passwordReset: true,
      portal: 'MetLife provider portal',
      resetUrl: 'metlife.com → Provider sign-in → Forgot password',
      accountUsername: 'alamoheightsdental_rcm',
      blocksLine: 'Blocks status on 14 claims and 6 remittances · $8,240',
      savePrimary: 'Save and let ELVA test it',
      nextMeta: 'ELVA signs in with the new password to check it works. If MetLife accepts it, this closes and the portal shows Working in Settings › Payer portals. If MetLife rejects it, the field clears and this stays open.',
      cantDo: {
        title: 'Can’t reset it?',
        reasons: ['We don’t have access to this account', 'The account belongs to someone who left', 'Something else'],
        notice: 'ELVA keeps using phone calls for MetLife instead, and marks the portal Not connected in Settings › Payer portals. Remittances by phone post slower.',
      },
      history: ['password expired Oct 14 · ELVA called MetLife Oct 15 · came here Oct 14'],
    },

    // Today · 1 — document (D122). Document Center links Bluebonnet here.
    {
      id: 'HL-BLUEBONNET',
      kind: 'document',
      kindLabel: 'Read a document',
      group: 'now',
      title: 'Read 2 amounts on Bluebonnet’s EOB',
      metaLine: 'Bluebonnet Dental Plan · check 30071 · $412.80 · faxed today 10:20 AM',
      deadlineText: 'Faxed today',
      deadlineWarn: true,
      why: 'Two paid amounts on Adam Ross’s lines are smudged on the fax, so ELVA can’t post them. Bluebonnet has no provider portal and no fee schedule on file, and will mail a clean copy in 7–10 days. You can read them off the paper now.',
      tries: [
        { time: 'Oct 15, 10:21 AM', channel: 'Check total', result: 'Two amounts are smudged, so the total can’t tell which is which.', ok: false, logEntryId: 'log-doc-delta' },
        { time: 'Oct 15, 10:22 AM', channel: 'Fee schedule', result: 'No fee schedule on file for Bluebonnet.', ok: false, logEntryId: 'log-doc-guardian' },
        { time: 'Oct 15, 10:24 AM', channel: 'Portal', result: 'Bluebonnet has no provider portal.', ok: false, logEntryId: 'log-portal-guardian' },
        { time: 'Oct 15, 10:31 AM', channel: 'Call to Bluebonnet', result: 'They’ll mail a clean copy in 7–10 days. Reference BB-55102.', ok: false, logEntryId: 'log-call-aetna' },
      ],
      docRead: {
        fields: [
          { id: 'd1110', label: 'Adam Ross · D1110 · paid' },
          { id: 'd0274', label: 'Adam Ross · D0274 · paid' },
        ],
        constraintText: 'They must add up to $144.80.',
      },
      savePrimary: 'Save and let ELVA post it',
      nextMeta: 'ELVA posts Bluebonnet’s check 30071 with these amounts and closes this.',
      cantDo: {
        title: 'Can’t read them?',
        reasons: ['The paper is unreadable too', 'Something else'],
        notice: 'ELVA waits for Bluebonnet’s clean copy and posts the check when it arrives.',
      },
      history: ['faxed 10:20 AM · ELVA read it 10:21 AM · came here Oct 15'],
      doneFindings: ['ELVA posted Bluebonnet’s check 30071 and closed this.'],
    },

    // Before tomorrow · 1 — patient
    {
      id: 'HL-PATIENT',
      kind: 'patient',
      kindLabel: 'Ask the patient',
      group: 'tomorrow',
      title: 'Which BlueCross plan does Patricia Davis have?',
      metaLine: 'Appointment tomorrow, Oct 16, 10:30 AM · cleaning and exam · Dr. Emily Carter',
      deadlineText: 'Tomorrow',
      deadlineWarn: false,
      why: 'Each BlueCross plan is a separate company with its own payer ID. Without knowing which, ELVA can’t check her coverage before the visit or send the claim after it.',
      tries: [
        { time: 'Oct 12, 2:10 PM', channel: 'Coverage check', result: 'Searched her member ID on the BlueCross plans for Texas and nearby states. No plan recognised it.', ok: false, logEntryId: 'log-portal-metlife' },
        { time: 'Oct 13, 10:05 AM', channel: 'Text to Patricia', result: 'Asked for a photo of both sides of her card. No reply.', ok: false, logEntryId: 'log-doc-delta' },
        { time: 'Oct 14, 4:30 PM', channel: 'Call to Patricia', result: 'Voicemail. ELVA left a message with the office number.', ok: false, logEntryId: 'log-call-aetna' },
        { time: 'Oct 15, 9:00 AM', channel: 'Text to Patricia', result: 'Asked again. No reply.', ok: false, logEntryId: 'log-doc-guardian' },
      ],
      photoPrompt: 'A photo of her card, both sides',
      plans: [
        'BlueCross BlueShield of Texas',
        'Anthem Blue Cross Blue Shield',
        'Blue Cross Blue Shield of Illinois',
        'Highmark Blue Cross Blue Shield',
        'Federal Employee Program (FEP)',
      ],
      memberId: 'XGP448210763',
      checkInAvailable: true,
      savePrimary: 'Save and let ELVA check',
      nextMeta: 'ELVA checks her coverage right away. This closes when the check comes back.',
      cantDo: {
        title: 'Can’t ask Patricia?',
        reasons: ['She cancelled the appointment', 'She doesn’t know her plan either', 'She’ll pay herself this time', 'Something else'],
        notice: 'ELVA stops asking and notes it on Patricia’s account. If she books again, ELVA asks again.',
      },
      history: ['card added at booking Oct 12 · ELVA tried 4 times Oct 12–15 · came here Oct 15, 9:01 AM'],
      doneFindings: [
        'Active through Dec 31, 2026. Cleaning and exam covered at 100%.',
        `ELVA saved the plan on Patricia’s insurance in ${PMS}.`,
      ],
    },

    // Removed per HITL_TASK_CATALOG §6/D154: a clinical reason like "why a
    // crown and not a filling" never texts the dentist from here. ELVA reads
    // it from the chart note; if it can't, the claim goes to Claims to
    // review, where the reason comes from the chart — not from staff
    // picking an option on Dr. Brooks's behalf.

    // This week · 1 — call
    {
      id: 'HL-CALL',
      kind: 'call',
      kindLabel: 'Make a call',
      group: 'week',
      title: 'Call Cigna about Aisha Patel’s claim',
      metaLine: 'Claim of Sep 21 · $210.00 · Cigna has held it for 24 days',
      deadlineText: 'By Oct 20',
      deadlineWarn: false,
      why: 'On Oct 14 the Cigna rep wouldn’t continue with an automated caller and asked for someone from the office. ELVA tried a second time and got the same answer.',
      tries: [
        { time: 'Oct 12, 9:20 AM', channel: 'Cigna’s portal', result: 'The claim shows “pending, additional review” with no reason.', ok: false, logEntryId: 'log-portal-metlife' },
        { time: 'Oct 14, 11:02 AM', channel: 'Call to Cigna, 9 min', result: 'The rep asked to speak with office staff. Reference 0F-2214-1180.', ok: false, logEntryId: 'log-call-aetna' },
        { time: 'Oct 15, 8:40 AM', channel: 'Call to Cigna, 7 min', result: 'Same answer from a second rep.', ok: false, logEntryId: 'log-call-metlife' },
      ],
      forCall: {
        number: 'Cigna dental provider services, 1-800-244-6224',
        ask: 'Ask why the claim is held and what Cigna needs to pay it.',
        facts: ['Tax ID 74-2918830', 'claim 2026-4410-0921', 'Aisha Patel, member U55-201-448', 'visit Sep 21', 'references from earlier calls: 0F-2214-1180'],
      },
      callOutcomes: [
        { label: 'Paid, or will pay by a date', needsDate: true },
        { label: 'They need something', needsDetail: 'What do they need? e.g. x-rays, narrative, perio chart, other coverage' },
        { label: 'It’s denied', needsDetail: 'The reason they gave' },
        { label: 'They never got it' },
        { label: 'Still in review, call back after a date', needsDate: true },
        { label: 'Something else', needsDetail: 'What happened' },
      ],
      savePrimary: 'Save and let ELVA follow up',
      nextMeta: 'ELVA reads what you wrote and does the next step: sends what Cigna needs, or checks back on the date they gave.',
      cantDo: {
        title: 'Can’t make the call?',
        reasons: ['Cigna won’t talk to us either', 'Something else'],
        notice: 'ELVA sends Cigna a written inquiry by mail and follows up from the delivery date.',
      },
      history: ['claim held since Sep 21 · ELVA tried 3 times Oct 12–15 · came here Oct 15'],
      doneFindings: ['ELVA read your note and is taking the next step with Cigna.'],
    },

    // This week · 2 — Decide about a claim (D147/D159). Insurance A/R links
    // AR-SANA and AR-VICTOR here; these are the canonical items.
    {
      id: 'HL-SANA',
      kind: 'claim',
      kindLabel: 'Decide about a claim',
      group: 'week',
      title: 'Sana Malik · MetLife hasn’t paid after 95 days',
      metaLine: 'MetLife · $766.00 · sent Jul 12 · claim 553-90-2217-02',
      deadlineText: 'At your follow-up limit',
      deadlineWarn: false,
      why: 'ELVA followed up 3 times, the limit you set, and MetLife still hasn’t paid or given a date. 95 days since the claim was sent. Someone decides: let ELVA keep going, or stop and write it off.',
      tries: [
        { time: 'Jul 12', channel: 'Sent', result: 'Accepted by the clearinghouse. MetLife received it the same day.', ok: true, logEntryId: 'log-call-metlife' },
        { time: 'Aug 6', channel: 'Follow-up 1 · status check', result: 'MetLife: “in process”.', ok: false, logEntryId: 'log-portal-metlife' },
        { time: 'Sep 2', channel: 'Follow-up 2 · status check', result: 'Still “in process”, no date given.', ok: false, logEntryId: 'log-portal-guardian' },
        { time: 'Oct 1', channel: 'Follow-up 3 · call to MetLife', result: '“In process”, escalated. Reference ML-77421-3.', ok: false, logEntryId: 'log-call-aetna' },
      ],
      claimDecide: {
        amount: '$766.00',
        checkDate: 'Oct 29',
        dates: ['Oct 29', 'Nov 12', 'Nov 26'],
        moreRounds: 3,
        stopReasons: ['Write it off', 'The patient will pay it', 'Billed to another insurance', 'Something else'],
        patientPayHelper: `ELVA moves $766 to Sana’s balance in ${PMS} and logs it. Collecting it is ELVA Core’s job.`,
        patientFirst: 'Sana',
      },
      savePrimary: 'Keep going',
      nextMeta: `Keep going runs 3 more rounds, then it comes back here. Stop writes it off in ${PMS}.`,
      cantDo: {
        title: 'Not sure yet?',
        reasons: ['Need to ask the office', 'Something else'],
        notice: 'ELVA keeps this claim out of new follow-ups until someone decides.',
      },
      history: ['sent Jul 12 · 3 follow-ups by ELVA Jul–Oct · came here Oct 15'],
      doneFindings: ['ELVA has your decision and is handling Sana Malik’s claim.'],
    },
    {
      id: 'HL-VICTOR',
      kind: 'claim',
      kindLabel: 'Decide about a claim',
      group: 'week',
      title: 'Victor Lang · United Concordia hasn’t paid after 86 days',
      metaLine: 'United Concordia · $604.00 · sent Jul 21 · claim UC88120473',
      deadlineText: 'At your follow-up limit',
      deadlineWarn: false,
      why: 'ELVA followed up 3 times, the limit you set. United Concordia keeps saying “in review” and gives no date. 86 days since the claim was sent.',
      tries: [
        { time: 'Jul 21', channel: 'Sent', result: 'Accepted by the clearinghouse. United Concordia received it the same day.', ok: true, logEntryId: 'log-post-jorge' },
        { time: 'Aug 6', channel: 'Follow-up 1 · status check', result: 'United Concordia: “in review”.', ok: false, logEntryId: 'log-portal-guardian' },
        { time: 'Aug 22', channel: 'Follow-up 2 · status check', result: 'Still “in review”, no date given.', ok: false, logEntryId: 'log-doc-guardian' },
        { time: 'Sep 12', channel: 'Follow-up 3 · call', result: '“In review”, escalated. Reference UC-55210-9.', ok: false, logEntryId: 'log-call-aetna' },
      ],
      claimDecide: {
        amount: '$604.00',
        checkDate: 'Oct 29',
        dates: ['Oct 29', 'Nov 12', 'Nov 26'],
        moreRounds: 3,
        stopReasons: ['Write it off', 'The patient will pay it', 'Billed to another insurance', 'Something else'],
        patientPayHelper: `ELVA moves $604 to Victor’s balance in ${PMS} and logs it. Collecting it is ELVA Core’s job.`,
        patientFirst: 'Victor',
      },
      savePrimary: 'Keep going',
      nextMeta: `Keep going runs 3 more rounds, then it comes back here. Stop writes it off in ${PMS}.`,
      cantDo: {
        title: 'Not sure yet?',
        reasons: ['Need to ask the office', 'Something else'],
        notice: 'ELVA keeps this claim out of new follow-ups until someone decides.',
      },
      history: ['sent Jul 21 · 3 follow-ups by ELVA Aug–Sep · came here Oct 15'],
      doneFindings: ['ELVA has your decision and is handling Victor Lang’s claim.'],
    },

    // Later · 1 — money
    {
      id: 'HL-MONEY',
      kind: 'money',
      kindLabel: 'Paper and money',
      group: 'later',
      title: 'A Guardian check never reached the bank',
      metaLine: 'Check 6610032 · $1,148.00 · 3 claims · posted Sep 30 from a faxed EOB',
      deadlineText: 'Posted Sep 30',
      deadlineWarn: false,
      why: 'ELVA posted this check from a faxed EOB on Sep 30, but no deposit for it has reached the bank, and Guardian’s portal shows it mailed but not cashed.',
      tries: [
        { time: 'Oct 7', channel: 'Bank statement, Sep 28–Oct 6', result: 'No deposit of $1,148.00.', ok: false, logEntryId: 'log-doc-delta' },
        { time: 'Oct 14', channel: 'Bank statement, Oct 1–14', result: 'Still no deposit of $1,148.00.', ok: false, logEntryId: 'log-doc-guardian' },
        { time: 'Oct 14, 3:05 PM', channel: 'Guardian’s portal', result: 'Guardian shows the check as mailed Sep 26 and not yet cashed.', ok: false, logEntryId: 'log-portal-guardian' },
      ],
      moneyChoices: ['We have it, we’ll deposit it', 'We deposited it with other checks', 'It went to another account', 'We can’t find it'],
      moneyExtras: {
        'We deposited it with other checks': { kind: 'date', label: 'The day you deposited it' },
        'It went to another account': { kind: 'text', label: 'Which account?' },
      },
      moneyNotes: {
        'We have it, we’ll deposit it': 'ELVA watches the next two statements. If it’s still missing, this item comes back.',
        'We deposited it with other checks': 'ELVA matches it inside that day’s combined deposit. It closes when the match is found.',
        'It went to another account': 'ELVA notes it. That account’s statements can be uploaded too.',
        'We can’t find it': 'This is the decision to stop payment. ELVA calls Guardian to stop the check and send the money again, by EFT if Guardian allows. The claims show “Reissue requested”, and this closes when the matching deposit is found.',
      },
      savePrimary: 'Save and let ELVA handle it',
      nextMeta: 'ELVA does something different depending on your answer — see the note once you pick one.',
      cantDo: {
        title: 'Can’t answer this?',
        reasons: ['Need to check with the office manager', 'Something else'],
        notice: 'ELVA calls Guardian to trace the check and reports what it finds.',
      },
      history: ['posted from a faxed EOB Sep 30 · ELVA checked 3 times Oct 7–14 · came here Oct 14'],
      doneFindings: ['ELVA is handling the check with Guardian.'],
    },
  ];
}

export function buildDone(): DoneItem[] {
  return [
    { id: 'd-1', kindLabel: 'Ask the patient', title: 'Noah Kim’s MetLife member ID', by: 'Ava Thompson (Lone Star Billing)', outcome: 'ELVA checked coverage and sent the claim.' },
    { id: 'd-3', kindLabel: 'Make a call', title: 'MetLife about Leah Morgan’s claim', by: 'Ava Thompson (Lone Star Billing)', outcome: 'MetLife reprocessed the claim.' },
    { id: 'd-4', kindLabel: 'Ask the dentist', title: 'Reason for the extraction on #30', by: 'Ava Thompson (Lone Star Billing)', outcome: 'ELVA wrote the narrative and sent the claim.' },
    { id: 'd-5', kindLabel: 'Ask the patient', title: 'Which plan does Diego Peña have?', by: 'Ava Thompson (Lone Star Billing)', outcome: 'Closed without an answer: patient cancelled.' },
  ];
}
